/**
 * Group lifecycle primitive for map-asset services (useAddAssets, useDroneEntity).
 *
 * Provides a generation token + AbortController per "active group". Any async
 * work spawned during `start(groupId)` can:
 *   1. Capture `lifecycle.generation` at entry
 *   2. Pass `lifecycle.signal` into abortable APIs (fetch, terrain sampling, etc.)
 *   3. Check `lifecycle.isStale(gen)` after every `await` boundary to drop writes
 *      that belong to a superseded group.
 *
 * `dispose()` is async because it yields one microtask so in-flight awaits
 * observe the abort before a new `start()` runs.
 */
export interface GroupLifecycle {
  readonly name: string
  readonly generation: number
  readonly activeGroupId: string | null
  readonly signal: AbortSignal
  isStale(gen: number): boolean
  start(groupId: string): { gen: number; signal: AbortSignal }
  dispose(): Promise<void>
}

const alreadyAborted = (): AbortSignal => {
  const ac = new AbortController()
  ac.abort()
  return ac.signal
}

export const createGroupLifecycle = (name: string): GroupLifecycle => {
  let generation = 0
  let activeGroupId: string | null = null
  let ac: AbortController | null = null

  return {
    name,
    get generation() {
      return generation
    },
    get activeGroupId() {
      return activeGroupId
    },
    get signal() {
      return ac?.signal ?? alreadyAborted()
    },
    isStale(gen: number) {
      return gen !== generation || !!ac?.signal.aborted
    },
    start(groupId: string) {
      generation++
      activeGroupId = groupId
      ac?.abort()
      ac = new AbortController()
      return { gen: generation, signal: ac.signal }
    },
    async dispose() {
      generation++
      ac?.abort()
      ac = null
      activeGroupId = null
      await Promise.resolve()
    },
  }
}

/**
 * True when an error is an AbortError (from AbortSignal-aware API).
 * Use at call sites to silently swallow expected cancellations instead of
 * logging them as real failures.
 */
export const isAbortError = (err: unknown): boolean => {
  if (!err) return false
  if (err instanceof DOMException && err.name === 'AbortError') return true
  if (typeof err === 'object' && err !== null && (err as { name?: string }).name === 'AbortError')
    return true
  return false
}
